import React, { useMemo, useRef } from 'react';
import { LayoutDashboardLeftMenuUserStyle as S } from './LayoutDashboardLeftMenuUser.style';
import { Avatar } from 'components/Avatar/Avatar';
import { Text } from 'components/Text/Text';
import { PMenu } from 'components/prime/PMenu/PMenu';
import { Menu } from 'primereact/menu';
import { useObservableEagerState, useObservableState } from 'observable-hooks';
import { getUserStore } from 'stores/user.store';
import { Box } from 'components/layouts/primitives/Box/Box';
import { capitalize } from 'utils/capitalize';
import { NavigateFunction, useNavigate } from 'react-router';
import { MenuItem } from 'primereact/menuitem';
import { DropdownMenuItem } from 'components/DropdownMenuItem/DropdownMenuItem';
import { FormattedNumber } from 'components/FormattedNumber/FormattedNumber';
import { getCustomerStore } from 'stores/customer.store';
import { getBillingPlanStore } from 'stores/billingPlan.store';
import { Icon } from 'components/Icon/Icon';
import { getFFStore } from 'stores/ff.store';

const getItems: (options?: {
  isOwner?: boolean;
  userEmail?: string;
  userFullName?: string;
  credits?: number;
  slug?: string;
  userRole?: 'administrator' | 'user' | undefined;
  navigate?: NavigateFunction;
  isYouGovUser?: boolean;
}) => MenuItem[] = (options = {}) => {
  const { isOwner, userEmail, userFullName, credits, userRole, navigate, isYouGovUser } = options;

  const userDataItem = {
    template: () => (
      <DropdownMenuItem isMuted variableHeight>
        <S.MenuItem gap="gap0">
          {userFullName && (
            <Text
              as="p"
              color="neutral600"
              size="fontSizeSM"
              lineHeight="lineHeightMD"
              weight="medium"
              testId="label-top-bar-user-fullname"
            >
              {userFullName}
            </Text>
          )}
          <Text
            ellipsis
            as="p"
            color="neutral500"
            size="fontSizeXS"
            wordBreak="break-all"
            testId="label-top-bar-user-email"
          >
            {userEmail}
          </Text>
        </S.MenuItem>
      </DropdownMenuItem>
    ),
  };

  const defaultItems: MenuItem[] = isYouGovUser
    ? [
        userDataItem,
        {
          label: 'Log out',
          command: () => (window.location.href = `${window.location.origin}/logout`),
        },
      ]
    : [
        userDataItem,
        { separator: true },
        {
          template: () => (
            <DropdownMenuItem
              variableHeight
              isMuted={userRole === 'user'}
              onClick={() => {
                if (userRole !== 'user') navigate?.('/settings/billing');
              }}
            >
              <S.MenuItem gap="gap0">
                <Text
                  as="p"
                  color="neutral600"
                  size="fontSizeXS"
                  transform="uppercase"
                  lineHeight="lineHeightMD"
                  weight="medium"
                >
                  Available credits
                </Text>
                <Text color="neutral500" testId="label-top-bar-credits">
                  {credits !== undefined ? <FormattedNumber num={credits} /> : '...'}
                </Text>
              </S.MenuItem>
            </DropdownMenuItem>
          ),
        },
        { separator: true },
        {
          label: 'Administration',
          command: () => (window.location.href = `${window.location.origin}/admin`),
        },
        {
          label: 'Settings',
          command: () => (window.location.href = `${window.location.origin}/settings/profile`),
        },
        {
          label: 'Log out',
          command: () => (window.location.href = `${window.location.origin}/logout`),
        },
      ];

  if (!isOwner) return defaultItems.filter((item) => item.label !== 'Administration');

  return defaultItems;
};

export const LayoutDashboardLeftMenuUser: React.FC = () => {
  const ref = useRef<Menu>(null);
  const navigate = useNavigate();
  const user = useObservableState(getUserStore().$user);
  const fullName = capitalize(`${user?.firstName || ''} ${user?.lastName || ''}`.trim());
  const userRole = useObservableState(getUserStore().$userRole);
  const customer = useObservableState(getCustomerStore().$customer);
  const credits = useObservableState(getBillingPlanStore().$creditsBalance);
  const isYouGovUser = useObservableEagerState(getFFStore().$hasFF('ff-restricted-yg-company'));

  const links = useMemo(
    () =>
      getItems({
        isOwner: user?.isYabbleOwner,
        userEmail: user?.email,
        userFullName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        credits,
        slug: customer?.slug,
        userRole,
        navigate,
        isYouGovUser,
      }),
    [user, userRole, customer, credits, navigate, isYouGovUser]
  );

  return (
    <S.Container>
      <S.Button
        severity="ghost"
        className="left-menu-user"
        onClick={(event) => ref?.current?.toggle(event)}
        data-testid="button-top-bar-user"
      >
        <Box fit="content">
          <Avatar
            color="rose600"
            bgColorAlpha={0.08}
            bgColor="rose500"
            size="lg"
            user={{ id: 12, name: fullName }}
          />
        </Box>
        <Box fit="space">
          <Text ellipsis className="button-custom-label" wordBreak="break-all">
            {fullName}
          </Text>
        </Box>
        <Icon icon="dotsHorizontalLine" size={24} />
      </S.Button>
      <PMenu ref={ref} model={links} appendTo="self" popupAlignment="right" popup />
    </S.Container>
  );
};
